import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as Yup from 'yup'
import {
  Alert,
  AlertTitle,
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Collapse,
  Grid,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import { LoadingButton } from '@mui/lab'
import UploadIcon from '@mui/icons-material/Upload'
import RefreshIcon from '@mui/icons-material/Refresh'
import HistoryIcon from '@mui/icons-material/History'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import { useSnackbar } from 'notistack'
import { CSVLink } from 'react-csv'
import { FormProvider, RHFFileField } from '../../common/hook-form'
import { apiConstants, getGeneric, postMultipart } from '../../common/constants'
import DownloadSampleFile from '../../common/DownloadSampleFile'
import PageTitle from '../../common/PageTitle'
import PageHero from '../../common/PageHero'
import withLayout from '../../hocs/Layout'

const POLL_INTERVAL_MS = 4000
const POLL_MAX_TICKS = 90

/**
 * Bulk-mark PTL shipments as RTO_REQUESTED via CSV.
 *
 * Layout mirrors proship-backend's {@code /uploads/edd} page — single Upload
 * card, chip-row results card, chip-grid for valid reasons. Submission is
 * async — backend queues tasks via TaskHandlerRegistry, returns a tracking
 * record immediately; this page polls until the job completes.
 */
function BulkMarkRtoRequested() {
  const { enqueueSnackbar } = useSnackbar()
  /** Last 7 days of this merchant's bulk uploads. Source of truth — both the
   *  "Recent Uploads" table and the "Upload Result" detail card read from here. */
  const [history, setHistory] = useState([])
  const [historyLoading, setHistoryLoading] = useState(false)
  /** uuid of the bulk file currently expanded in the detail card. */
  const [selectedUuid, setSelectedUuid] = useState(null)
  const [reasons, setReasons] = useState([])
  const [reasonsLoading, setReasonsLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const pollTimerRef = useRef(null)
  const pollTickRef = useRef(0)

  const methods = useForm({
    resolver: yupResolver(
      Yup.object().shape({
        file: Yup.mixed().required('Please select a CSV file'),
      }),
    ),
    defaultValues: { file: null },
  })
  const { handleSubmit, reset } = methods

  // -- Load reasons (valid-codes reference) -------------------------------
  useEffect(() => {
    let cancelled = false
    const load = async () => {
      setReasonsLoading(true)
      try {
        const res = await getGeneric(apiConstants.REASON_MASTER, {
          category: 'RTO_REQUEST',
          activeOnly: 'true',
        })
        const list = Array.isArray(res?.data) ? res.data : res?.data?.responseObj || []
        if (!cancelled) {
          const sorted = [...list].sort((a, b) => {
            const ao = a.displayOrder == null ? Number.MAX_SAFE_INTEGER : a.displayOrder
            const bo = b.displayOrder == null ? Number.MAX_SAFE_INTEGER : b.displayOrder
            if (ao !== bo) return ao - bo
            return (a.label || '').localeCompare(b.label || '')
          })
          setReasons(sorted)
        }
      } catch (e) {
        if (!cancelled) {
          enqueueSnackbar(
            e?.response?.data?.message || 'Failed to load reason codes',
            { variant: 'error' },
          )
        }
      } finally {
        if (!cancelled) setReasonsLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [enqueueSnackbar])

  // -- History loader + poller --------------------------------------------
  // Load on mount + after each successful submit. Whenever loaded, the
  // currently-selected uuid (if any) auto-refreshes from the new data —
  // gives free polling for free.
  useEffect(() => {
    loadHistory()
    return () => stopPolling()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const loadHistory = async () => {
    setHistoryLoading(true)
    try {
      const res = await getGeneric(apiConstants.MARK_RTO_REQUESTED_BULK_STATUS, {})
      const list = res?.data?.responseObj || []
      // Backend doesn't guarantee order — sort newest-first by createdAt.
      list.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
      setHistory(list)
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('Failed to load bulk history', e)
    } finally {
      setHistoryLoading(false)
    }
  }

  const stopPolling = () => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current)
      pollTimerRef.current = null
    }
    pollTickRef.current = 0
  }

  const startPolling = (uuid) => {
    stopPolling()
    pollOnce(uuid)
    pollTimerRef.current = setInterval(() => {
      pollTickRef.current += 1
      if (pollTickRef.current >= POLL_MAX_TICKS) {
        stopPolling()
        enqueueSnackbar(
          'Bulk job is taking longer than expected. Hit refresh to keep polling.',
          { variant: 'info' },
        )
        return
      }
      pollOnce(uuid)
    }, POLL_INTERVAL_MS)
  }

  const pollOnce = async (uuid) => {
    try {
      const res = await getGeneric(apiConstants.MARK_RTO_REQUESTED_BULK_STATUS, {})
      const list = res?.data?.responseObj || []
      list.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
      setHistory(list)
      const match = list.find((r) => r.uuid === uuid)
      if (match && match.fileProgress === 'COMPLETED') stopPolling()
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('Bulk RTO status poll failed', e)
    }
  }

  const copyCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code)
      enqueueSnackbar(`Copied: ${code}`, { variant: 'success' })
    } catch (e) {
      enqueueSnackbar('Copy failed — select and copy manually', { variant: 'warning' })
    }
  }

  // -- Submit --------------------------------------------------------------
  const onSubmit = async (data) => {
    const file = data.file?.[0]
    if (!file) {
      enqueueSnackbar('Please select a CSV file', { variant: 'warning' })
      return
    }
    if (!/\.csv$/i.test(file.name)) {
      enqueueSnackbar('Only .csv files are accepted', { variant: 'warning' })
      return
    }
    if (file.size > 5 * 1024 * 1024) {
      enqueueSnackbar('File too large. Maximum 5 MB.', { variant: 'warning' })
      return
    }
    setIsSubmitting(true)
    setSelectedUuid(null)
    try {
      const formData = new FormData()
      formData.append('file', file)
      const res = await postMultipart(apiConstants.MARK_RTO_REQUESTED_BULK, formData)
      const status = res?.data?.status
      const obj = res?.data?.responseObj
      if (status === 'SUCCESS' && obj?.uuid) {
        setSelectedUuid(obj.uuid)
        // Refresh history immediately so the new row appears at the top.
        loadHistory()
        enqueueSnackbar('Bulk job queued — processing in background', { variant: 'success' })
        startPolling(obj.uuid)
        reset({ file: null })
      } else {
        const msg = res?.data?.errorMsg || res?.data?.responseObj || 'Bulk upload failed'
        enqueueSnackbar(typeof msg === 'string' ? msg : 'Bulk upload failed', { variant: 'error' })
      }
    } catch (err) {
      enqueueSnackbar(
        err?.response?.data?.errorMsg ||
          err?.response?.data?.message ||
          err?.message ||
          'Bulk upload failed',
        { variant: 'error' },
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  /** True when the currently-expanded row's job is still running — used to
   *  disable the Upload button so we don't queue another file while polling. */
  const selectedInProgress = useMemo(() => {
    if (!selectedUuid) return false
    const row = history.find((r) => r.uuid === selectedUuid)
    return row && row.fileProgress !== 'COMPLETED'
  }, [history, selectedUuid])

  return (
    <div id='main-content' className='container-fluid'>
      <PageTitle>Bulk Mark RTO Requested</PageTitle>
      {/* <PageHero title='Bulk Mark RTO Requested' subtitle='Bulk mark PTL shipments as RTO requested via CSV' sx={{ mb: 2.5 }} /> */}

      <Alert severity='info' sx={{ mb: 2 }}>
        <AlertTitle>How it works</AlertTitle>
        Upload a CSV with columns <Box component='code' sx={{ fontFamily: 'monospace' }}>AWB Number</Box>,{' '}
        <Box component='code' sx={{ fontFamily: 'monospace' }}>RTO Reason Code</Box>, and{' '}
        <Box component='code' sx={{ fontFamily: 'monospace' }}>Remarks</Box>. The job is processed in the
        background — this page polls every {POLL_INTERVAL_MS / 1000}s for progress. Reasons and remarks
        are shared with the courier — do not include customer phone numbers, emails, or other PII.
        <br />
        <strong>Limits:</strong> max 500 rows per upload, 5 MB file size, remarks 10–1000 characters.
      </Alert>

      {/* Upload form ---------------------------------------------------- */}
      <Card sx={{ mb: 2 }}>
        <CardHeader title='Upload RTO Marks' />
        <CardContent>
          <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <RHFFileField name='file' />
              </Grid>
              <Grid item xs={12} sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                <LoadingButton
                  loading={isSubmitting}
                  variant='contained'
                  size='medium'
                  startIcon={<UploadIcon />}
                  type='submit'
                  color='warning'
                  disabled={selectedInProgress}
                >
                  Upload
                </LoadingButton>
                <DownloadSampleFile fileName='B2B_RTO_MARK' label='Download Sample' size='medium' />
              </Grid>
            </Grid>
          </FormProvider>
        </CardContent>
      </Card>

      {/* Valid reasons reference --------------------------------------- */}
      {!reasonsLoading && reasons.length > 0 && (
        <Card variant='outlined'>
          <CardHeader
            title='Valid RTO Reason Codes'
            subheader='Each row in your CSV must use one of these codes in the RTO Reason Code column.'
            titleTypographyProps={{ variant: 'subtitle1' }}
          />
          <CardContent>
            <Grid container spacing={1}>
              {reasons.map((r) => (
                <Grid item key={r.id || r.code}>
                  <Tooltip title={r.label}>
                    <Chip
                      label={r.code}
                      variant='outlined'
                      color='primary'
                      size='small'
                      onClick={() => copyCode(r.code)}
                      sx={{ fontFamily: 'monospace', cursor: 'pointer' }}
                    />
                  </Tooltip>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      )}

      {!reasonsLoading && reasons.length === 0 && (
        <Alert severity='warning' sx={{ mb: 2 }}>
          No active RTO reasons are configured. Ask an admin to seed them in the Reason Master settings
          before using bulk upload.
        </Alert>
      )}

      {/* Recent uploads — last 7 days. Click a row to expand its detail in
          the Upload Result card above. Matches the NDR/WD bulk action history
          pattern so merchants can revisit past submissions. */}
      <Card variant='outlined' sx={{ mt: 2 }}>
        <CardHeader
          title='Recent Uploads (last 7 days)'
          titleTypographyProps={{ variant: 'subtitle1' }}
          avatar={<HistoryIcon color='action' />}
          action={
            <Tooltip title='Refresh list'>
              <IconButton size='small' onClick={loadHistory} disabled={historyLoading}>
                <RefreshIcon fontSize='small' />
              </IconButton>
            </Tooltip>
          }
        />
        <CardContent sx={{ pt: 0 }}>
          {historyLoading && history.length === 0 && (
            <Typography variant='body2' color='text.secondary'>
              Loading…
            </Typography>
          )}
          {!historyLoading && history.length === 0 && (
            <Typography variant='body2' color='text.secondary'>
              No bulk uploads in the last 7 days.
            </Typography>
          )}
          {history.length > 0 && (
            <TableContainer>
              <Table size='small'>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#fafafa' }}>
                    <TableCell sx={{ width: 48 }} />
                    <TableCell><strong>Submitted</strong></TableCell>
                    <TableCell><strong>By</strong></TableCell>
                    <TableCell align='right'><strong>Total</strong></TableCell>
                    <TableCell align='right'><strong>Success</strong></TableCell>
                    <TableCell align='right'><strong>Failed</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {history.map((row) => {
                    const exec = row.executionStatus || {}
                    const isDone = row.fileProgress === 'COMPLETED'
                    const isOpen = row.uuid === selectedUuid
                    // Local detail figures — same shape as the old `summary` memo
                    // but computed per row so the expansion content has full
                    // context without depending on global state.
                    const total = exec.totalTasks ?? 0
                    const success = exec.success ?? 0
                    const failed = exec.failed ?? 0
                    const completed = success + failed
                    const percent = total > 0 ? Math.min(100, Math.round((completed / total) * 100)) : 0
                    // Build per-row outcomes (both successes and failures)
                    // from taskListBatch. The full list is useful for offline
                    // reconciliation; the table below shows failures only so
                    // the UI stays focused on what the user needs to act on.
                    const rowAllResults = []
                    const rowFailures = []
                    if (Array.isArray(row.taskListBatch)) {
                      for (const batch of row.taskListBatch) {
                        if (!Array.isArray(batch)) continue
                        for (const task of batch) {
                          const resp = task?.response || {}
                          const ok = resp.success === true || task?.status === 'SUCCESS'
                          const item = {
                            awbNumber: resp.awbNumber || task?.data?.awbNumber || '—',
                            success: ok,
                            message: ok ? '' : (resp.message || task?.errorMSG || 'Unknown error'),
                          }
                          rowAllResults.push(item)
                          if (!ok) rowFailures.push(item)
                        }
                      }
                    }
                    return (
                      <React.Fragment key={row.uuid}>
                        <TableRow
                          hover
                          selected={isOpen}
                          sx={{ cursor: 'pointer', '& > *': { borderBottom: isOpen ? 'unset' : undefined } }}
                          onClick={() => setSelectedUuid(isOpen ? null : row.uuid)}
                        >
                          <TableCell>
                            <IconButton
                              size='small'
                              onClick={(e) => {
                                e.stopPropagation()
                                setSelectedUuid(isOpen ? null : row.uuid)
                              }}
                            >
                              {isOpen ? (
                                <KeyboardArrowUpIcon fontSize='small' />
                              ) : (
                                <KeyboardArrowDownIcon fontSize='small' />
                              )}
                            </IconButton>
                          </TableCell>
                          <TableCell>
                            {row.createdAt ? new Date(row.createdAt).toLocaleString() : '—'}
                          </TableCell>
                          <TableCell sx={{ fontSize: 13 }}>{row.createdBy || '—'}</TableCell>
                          <TableCell align='right'>{total}</TableCell>
                          <TableCell align='right' sx={{ color: 'success.main' }}>
                            {success}
                          </TableCell>
                          <TableCell
                            align='right'
                            sx={{ color: failed > 0 ? 'error.main' : 'text.primary' }}
                          >
                            {failed}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={isDone ? 'COMPLETED' : `${percent || 0}%`}
                              size='small'
                              color={isDone ? 'success' : 'warning'}
                              variant='outlined'
                            />
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ p: 0, borderBottom: isOpen ? undefined : 'none' }} colSpan={7}>
                            <Collapse in={isOpen} timeout='auto' unmountOnExit>
                              <Box sx={{ p: 2, bgcolor: '#fafbfc' }}>
                                <Box sx={{ mb: 2 }}>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                    <Typography variant='caption'>
                                      {completed} of {total} processed
                                    </Typography>
                                    <Typography variant='caption'>{percent}%</Typography>
                                  </Box>
                                  <LinearProgress
                                    variant='determinate'
                                    value={percent}
                                    color={isDone ? 'success' : 'warning'}
                                  />
                                  <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mt: 0.5 }}>
                                    {isDone
                                      ? 'Job finished.'
                                      : `Auto-refreshing every ${POLL_INTERVAL_MS / 1000}s — job runs in the background.`}
                                  </Typography>
                                </Box>

                                {rowFailures.length > 0 ? (
                                  <Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
                                      <Typography variant='subtitle2' color='error'>
                                        Failed rows ({rowFailures.length})
                                      </Typography>
                                      {/* Single "Download All Results" CSV — covers both successes
                                          and failures with a Status column. One file per upload,
                                          useful for audit + reconciliation. Failures-only is
                                          implicit since they're the rows where Status=FAILED. */}
                                      <CSVLink
                                        data={[
                                          ['Status', 'AWB Number', 'Failure Reason'],
                                          ...rowAllResults.map((r) => [
                                            r.success ? 'SUCCESS' : 'FAILED',
                                            r.awbNumber,
                                            r.message,
                                          ]),
                                        ]}
                                        filename={`rto_bulk_results_${row.uuid}.csv`}
                                        style={{ textDecoration: 'none' }}
                                      >
                                        <Chip
                                          label={`Download Results (${rowAllResults.length})`}
                                          size='small'
                                          variant='outlined'
                                          color='primary'
                                          clickable
                                        />
                                      </CSVLink>
                                    </Box>
                                    <TableContainer
                                      sx={{
                                        maxHeight: 480,
                                        border: '1px solid #eee',
                                        bgcolor: '#fff',
                                        // CSS containment hints to keep big tables responsive
                                        contain: 'content',
                                      }}
                                    >
                                      <Table size='small' stickyHeader>
                                        <TableHead>
                                          <TableRow>
                                            <TableCell sx={{ width: 64, bgcolor: '#fafafa' }}>
                                              <strong>#</strong>
                                            </TableCell>
                                            <TableCell sx={{ width: 220, bgcolor: '#fafafa' }}>
                                              <strong>AWB Number</strong>
                                            </TableCell>
                                            <TableCell sx={{ bgcolor: '#fafafa' }}>
                                              <strong>Failure Reason</strong>
                                            </TableCell>
                                          </TableRow>
                                        </TableHead>
                                        <TableBody>
                                          {rowFailures.map((r, i) => (
                                            <TableRow key={`${r.awbNumber}-${i}`}>
                                              <TableCell sx={{ color: 'text.secondary', fontSize: 12 }}>
                                                {i + 1}
                                              </TableCell>
                                              <TableCell sx={{ fontFamily: 'monospace', fontSize: 13 }}>
                                                {r.awbNumber}
                                              </TableCell>
                                              <TableCell sx={{ color: 'error.main' }}>
                                                {r.message}
                                              </TableCell>
                                            </TableRow>
                                          ))}
                                        </TableBody>
                                      </Table>
                                    </TableContainer>
                                    {rowFailures.length > 50 && (
                                      <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mt: 0.5 }}>
                                        Showing all {rowFailures.length} failures — scroll inside the table, or click "Download Failures CSV" for offline review.
                                      </Typography>
                                    )}
                                  </Box>
                                ) : isDone ? (
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                    <Typography variant='body2' color='success.main'>
                                      All {total} rows processed successfully.
                                    </Typography>
                                    {rowAllResults.length > 0 && (
                                      <CSVLink
                                        data={[
                                          ['Status', 'AWB Number', 'Failure Reason'],
                                          ...rowAllResults.map((r) => [
                                            r.success ? 'SUCCESS' : 'FAILED',
                                            r.awbNumber,
                                            r.message,
                                          ]),
                                        ]}
                                        filename={`rto_bulk_results_${row.uuid}.csv`}
                                        style={{ textDecoration: 'none' }}
                                      >
                                        <Chip
                                          label={`Download Results (${rowAllResults.length})`}
                                          size='small'
                                          variant='outlined'
                                          color='primary'
                                          clickable
                                        />
                                      </CSVLink>
                                    )}
                                  </Box>
                                ) : (
                                  <Typography variant='body2' color='text.secondary'>
                                    Waiting for completion…
                                  </Typography>
                                )}
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

export default withLayout(BulkMarkRtoRequested)
